import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import StatusBadge from '../../../components/ui/StatusBadge';
import { supabase } from '../../../lib/supabaseClient';

const UserManagementTable = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const itemsPerPage = 10;

  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [users, setUsers] = useState([]);
  const [hasNextPage, setHasNextPage] = useState(false);

  const offset = useMemo(() => (currentPage - 1) * itemsPerPage, [currentPage, itemsPerPage]);


  const roleOptions = [
  { value: 'all', label: '모든 역할' },
  { value: 'user', label: '사용자' },
  { value: 'admin', label: '관리자' }];

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, roleFilter]);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setLoadError('');
      try {
        const search = searchQuery?.trim() ? searchQuery.trim() : null;
        const role = roleFilter || 'all';

        const { data, error } = await supabase.rpc('ch_admin_list_users', {
          p_limit: itemsPerPage + 1,
          p_offset: offset,
          p_search: search,
          p_role: role,
        });

        if (error) {
          setUsers([]);
          setHasNextPage(false);
          setLoadError(error?.message || '유저 목록을 불러오지 못했습니다.');
          return;
        }

        const rows = data || [];
        setHasNextPage(rows.length > itemsPerPage);

        const pageRows = rows.slice(0, itemsPerPage);
        const mapped = pageRows.map((r) => ({
          id: r.user_id,
          name: r.username,
          role: r.role,
          avatar: r.avatar_url || '',
          avatarAlt: r.username ? `${r.username} avatar` : 'avatar',
          isPublic: r.is_public,
          registrationDate: r.created_at ? String(r.created_at).slice(0, 10) : '',
          totalProjects: Number(r.total_projects || 0),
          totalProofs: Number(r.total_proofs || 0),
          totalChallenges: Number(r.total_challenges || 0),
          lastActive: r.last_proof_at ? String(r.last_proof_at).slice(0, 10) : '',
        }));

        setUsers(mapped);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [itemsPerPage, offset, roleFilter, searchQuery]);

  const paginatedUsers = users;

  const handleSelectAll = (e) => {
    if (e?.target?.checked) {
      setSelectedUsers(paginatedUsers?.map((user) => user?.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers((prev) =>
    prev?.includes(userId) ? prev?.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleAction = (action, userId = null) => {
    setPendingAction({ action, userId });
    setShowConfirmDialog(true);
  };

  const confirmAction = () => {
    console.log('Action confirmed:', pendingAction);
    setShowConfirmDialog(false);
    setPendingAction(null);
    setSelectedUsers([]);
  };

  const getPublicBadge = (isPublic) => {
    return isPublic ? (
      <StatusBadge label="공개" tone="public" />
    ) : (
      <StatusBadge label="비공개" tone="private" />
    );
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex-1 max-w-md">
          <Input
            type="search"
            placeholder="닉네임으로 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)} />

        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <Select
            options={roleOptions}
            value={roleFilter}
            onChange={setRoleFilter}
            placeholder="역할 필터" />
        </div>
      </div>
      {loadError ? (
        <div className="bg-card rounded-lg shadow-gaming-md p-6">
          <p className="text-sm text-muted-foreground caption">{loadError}</p>
        </div>
      ) : null}
      {isLoading ? (
        <div className="bg-card rounded-lg shadow-gaming-md p-6">
          <p className="text-sm text-muted-foreground caption">로딩 중...</p>
        </div>
      ) : null}
      {!isLoading && !loadError && paginatedUsers?.length === 0 ? (
        <div className="bg-card rounded-lg shadow-gaming-md p-6">
          <p className="text-sm text-muted-foreground caption">표시할 유저가 없습니다.</p>
        </div>
      ) : null}
      {selectedUsers?.length > 0 &&
      <div className="flex flex-wrap items-center gap-3 p-4 bg-primary/10 rounded-lg border border-primary/20">
          <span className="text-sm font-medium text-foreground">
            {selectedUsers?.length}명 선택됨
          </span>
          <div className="flex flex-wrap gap-2">
            <Button
            variant="outline"
            size="sm"
            iconName="UserCheck"
            onClick={() => handleAction('activate')}>

              활성화
            </Button>
            <Button
            variant="outline"
            size="sm"
            iconName="UserX"
            onClick={() => handleAction('suspend')}>

              정지
            </Button>
            <Button
            variant="destructive"
            size="sm"
            iconName="Trash2"
            onClick={() => handleAction('delete')}>

              삭제
            </Button>
          </div>
        </div>
      }
      <div className="hidden lg:block bg-card rounded-lg shadow-gaming-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <th className="px-6 py-4 text-left">
                  <input
                    type="checkbox"
                    checked={selectedUsers?.length === paginatedUsers?.length && paginatedUsers?.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded border-border bg-background text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2" />

                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">사용자</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">역할</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">공개</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">최근 활동</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">통계</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">챌린지</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-foreground">가입일</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-foreground">작업</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {paginatedUsers?.map((user) =>
              <tr key={user?.id} className="hover:bg-muted/30 transition-gaming">
                  <td className="px-6 py-4">
                    <input
                    type="checkbox"
                    checked={selectedUsers?.includes(user?.id)}
                    onChange={() => handleSelectUser(user?.id)}
                    className="w-4 h-4 rounded border-border bg-background text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2" />

                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <Image
                      src={user?.avatar}
                      alt={user?.avatarAlt}
                      className="w-10 h-10 rounded-md object-cover" />

                      <div>
                        <div className="text-sm font-medium text-foreground">{user?.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-foreground capitalize">{user?.role === 'admin' ? '관리자' : '사용자'}</span>
                  </td>
                  <td className="px-6 py-4">{getPublicBadge(user?.isPublic)}</td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-muted-foreground caption">{user?.lastActive || '-'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col space-y-1">
                      <span className="text-sm text-foreground data-text">{user?.totalProjects} 프로젝트</span>
                      <span className="text-xs text-muted-foreground caption">{user?.totalProofs} 증명</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-foreground data-text">{user?.totalChallenges}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-muted-foreground caption">{user?.registrationDate}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                      onClick={() => handleAction('edit', user?.id)}
                      className="p-2 rounded-md hover:bg-muted transition-gaming"
                      aria-label="사용자 편집">

                        <Icon name="Edit2" size={16} className="text-muted-foreground" />
                      </button>
                      <button
                      onClick={() => handleAction('view', user?.id)}
                      className="p-2 rounded-md hover:bg-muted transition-gaming"
                      aria-label="사용자 보기">

                        <Icon name="Eye" size={16} className="text-muted-foreground" />
                      </button>
                      <button
                      onClick={() => handleAction('delete', user?.id)}
                      className="p-2 rounded-md hover:bg-destructive/10 transition-gaming"
                      aria-label="사용자 삭제">

                        <Icon name="Trash2" size={16} className="text-destructive" />
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="lg:hidden space-y-3">
        {paginatedUsers?.map((user) =>
        <div key={user?.id} className="bg-card rounded-lg shadow-gaming-sm p-4 space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3 flex-1 min-w-0">
                <input
                type="checkbox"
                checked={selectedUsers?.includes(user?.id)}
                onChange={() => handleSelectUser(user?.id)}
                className="w-4 h-4 rounded border-border bg-background text-primary focus:ring-2 focus:ring-primary flex-shrink-0" />

                <Image
                src={user?.avatar}
                alt={user?.avatarAlt}
                className="w-12 h-12 rounded-md object-cover flex-shrink-0" />

                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">{user?.name}</div>
                  <div className="text-xs text-muted-foreground caption truncate">{user?.lastActive || '-'}</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-muted-foreground caption mb-1">역할</div>
                <div className="text-sm text-foreground">{user?.role === 'admin' ? '관리자' : '사용자'}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground caption mb-1">공개</div>
                {getPublicBadge(user?.isPublic)}
              </div>
              <div>
                <div className="text-xs text-muted-foreground caption mb-1">가입</div>
                <div className="text-sm text-foreground">{user?.registrationDate}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground caption mb-1">통계</div>
                <div className="text-sm text-foreground data-text">{user?.totalProjects}개 / {user?.totalProofs}개</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground caption mb-1">챌린지</div>
                <div className="text-sm text-foreground data-text">{user?.totalChallenges}개</div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-border">
              <span className="text-xs text-muted-foreground caption">가입: {user?.registrationDate}</span>
              <div className="flex items-center space-x-2">
                <button
                onClick={() => handleAction('edit', user?.id)}
                className="p-2 rounded-md hover:bg-muted transition-gaming"
                aria-label="사용자 편집">

                  <Icon name="Edit2" size={16} className="text-muted-foreground" />
                </button>
                <button
                onClick={() => handleAction('view', user?.id)}
                className="p-2 rounded-md hover:bg-muted transition-gaming"
                aria-label="사용자 보기">

                  <Icon name="Eye" size={16} className="text-muted-foreground" />
                </button>
                <button
                onClick={() => handleAction('delete', user?.id)}
                className="p-2 rounded-md hover:bg-destructive/10 transition-gaming"
                aria-label="사용자 삭제">

                  <Icon name="Trash2" size={16} className="text-destructive" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {(currentPage > 1 || hasNextPage) &&
      <div className="flex items-center justify-between pt-4">
          <div className="text-sm text-muted-foreground caption">
            페이지 {currentPage}
          </div>
          <div className="flex items-center space-x-2">
            <Button
            variant="outline"
            size="sm"
            iconName="ChevronLeft"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1 || isLoading}>

              이전
            </Button>
            <Button
            variant="outline"
            size="sm"
            iconName="ChevronRight"
            iconPosition="right"
            onClick={() => setCurrentPage((prev) => prev + 1)}
            disabled={!hasNextPage || isLoading}>

              다음
            </Button>
          </div>
        </div>
      }
      {showConfirmDialog &&
      <div className="fixed inset-0 z-dialog bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card rounded-lg shadow-gaming-lg p-6 md:p-8 max-w-md w-full">
            <div className="flex items-start space-x-4 mb-6">
              <div className="flex-shrink-0 w-12 h-12 bg-warning/20 rounded-md flex items-center justify-center">
                <Icon name="AlertTriangle" size={24} color="var(--color-warning)" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">작업 확인</h3>
                <p className="text-sm text-muted-foreground">
                  이 작업을 계속하시겠습니까? 이 작업은 되돌릴 수 없습니다.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
                취소
              </Button>
              <Button variant="destructive" onClick={confirmAction}>
                확인
              </Button>
            </div>
          </div>
        </div>
      }
    </div>);

};

export default UserManagementTable;